'use client';

import { Share2Icon } from '@radix-ui/react-icons';
import { useTranslations } from 'next-intl';
import { forwardRef } from 'react';
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
  XIcon,
} from 'react-share';

import { Button, ButtonProps } from '@/app/_components/ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTrigger,
} from '@/app/_components/ui/drawer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/app/_components/ui/dropdown-menu';
import { useToast } from '@/app/_components/ui/use-toast';

interface ShareOptionsProps {
  pageName: string;
}

interface ShareProps {
  pageName: string;
  isPageLocked: boolean;
}

const ShareTrigger = forwardRef<HTMLButtonElement, ButtonProps>(
  (props, ref) => {
    return (
      <Button
        variant="ghost"
        size="icon"
        className="size-14"
        ref={ref}
        {...props}
      >
        <Share2Icon className="size-8" />
      </Button>
    );
  },
);

const ShareOptions = ({ pageName }: ShareOptionsProps) => {
  const t = useTranslations();
  const { toast } = useToast();

  const shareUrl = window.location.href;

  const copyToClipBoard = () => {
    navigator.clipboard.writeText(shareUrl);
    toast({
      variant: 'success',
      description: t('common.copiedToClipboard'),
    });
  };

  return (
    <div className="flex flex-col gap-4 p-2">
      <div className="flex justify-center gap-2">
        <FacebookShareButton url={shareUrl}>
          <FacebookIcon round className="size-10" />
        </FacebookShareButton>
        <LinkedinShareButton url={shareUrl}>
          <LinkedinIcon round className="size-10" />
        </LinkedinShareButton>
        <TwitterShareButton url={shareUrl} title={pageName}>
          <XIcon round className="size-10" />
        </TwitterShareButton>
        <EmailShareButton url={shareUrl}>
          <EmailIcon round className="size-10" />
        </EmailShareButton>
        <WhatsappShareButton url={shareUrl}>
          <WhatsappIcon round className="size-10" />
        </WhatsappShareButton>
      </div>
      <div className="flex items-center justify-between gap-2 rounded-sm border bg-muted p-2">
        <p className="text-sm">{shareUrl}</p>
        <Button
          className="rounded-full"
          size="sm"
          type="button"
          onClick={() => copyToClipBoard()}
        >
          {t('common.copy')}
        </Button>
      </div>
    </div>
  );
};

const Share = ({ pageName, isPageLocked }: ShareProps) => {
  return (
    <>
      <div className="hidden md:inline">
        <DropdownMenu>
          <DropdownMenuTrigger disabled={isPageLocked} asChild>
            <ShareTrigger />
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <ShareOptions pageName={pageName} />
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="md:hidden">
        <Drawer>
          <DrawerTrigger disabled={isPageLocked} asChild>
            <ShareTrigger />
          </DrawerTrigger>
          <DrawerContent>
            <DrawerDescription className="pb-20 pt-4">
              <ShareOptions pageName={pageName} />
            </DrawerDescription>
          </DrawerContent>
        </Drawer>
      </div>
    </>
  );
};

export default Share;
